// src/auth/EntraAuthProvider.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AuthContext } from './AuthContext';
import { entraConfigDiagnostics, isEntraConfigComplete, loginRequest } from './msalConfig';
import { AuthFailure, classifyAuthFailure } from './authFailure';
import { msalInstance } from './msalInstance';
import { getMe } from '../services/api';
import { ensureMsalInitialized} from './msalInstance';

function EntraAuthProviderInner({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated                     = useIsAuthenticated();

  const [associateId, setAssociateId] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [isLoading,   setIsLoading]   = useState(true);
  const [error,       setError]       = useState(null);
  // Set when Entra authenticated the user but the API would not grant access. Kept
  // separate from `error` because this is a state the user can act on (sign in as
  // themselves), not a fault to report.
  const [authFailure, setAuthFailure] = useState(null);

  // The signed-in UPN. The single most useful thing to show someone who has landed
  // on the wrong account — without it, "access denied" gives them nothing to act on.
  const userEmail = accounts[0]?.username ?? null;

  console.log('EntraAuthProviderInner render:', { 
    inProgress, 
    isAuthenticated, 
    accountCount: accounts.length 
  });

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (!isAuthenticated || accounts.length === 0) {
      setIsLoading(false);
      return;
    }

    instance.setActiveAccount(accounts[0]);

    (async () => {
      setIsLoading(true);
      setError(null);
      setAuthFailure(null);
      try {
        const res = await getMe();
        setAssociateId(res.data.associateId);
        setDisplayName(res.data.displayName);
      } catch (err) {
        const failure = classifyAuthFailure(err);
        setAuthFailure(failure);

        // Only a genuine misconfiguration is worth surfacing the diagnostic for; the
        // other two are ordinary situations with their own screen.
        if (failure === AuthFailure.CONFIG) {
          console.error(
            '[Auth] /auth/me failed.',
            err?.response?.status,
            err?.response?.data?.message ??
            'Check that EntraId:ClientId matches this app\'s client ID, ' +
            'and that the associateID claim is mapped onto the ID token.',
          );
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isAuthenticated, accounts, inProgress, instance]);

  const signIn = useCallback(async () => {
    setError(null);
    if (!isEntraConfigComplete()) {
      setError('Entra config incomplete — missing REACT_APP_ENTRA_TENANT_ID or REACT_APP_ENTRA_CLIENT_ID.');
      return;
    }
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  // logoutRedirect, not just a cache clear: dropping the local tokens would leave the
  // Entra session cookie alive, so the next sign-in would silently land on the same
  // account and loop straight back to this screen.
  const signOut = useCallback(() => {
    setAssociateId(null);
    setDisplayName(null);
    setAuthFailure(null);
    instance.logoutRedirect();
  }, [instance]);

  const value = useMemo(
    () => ({ associateId, displayName, userEmail, isLoading, error, authFailure, signIn, signOut }),
    [associateId, displayName, userEmail, isLoading, error, authFailure, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function EntraAuthProvider({ children }) {
  const [msalReady, setMsalReady] = useState(false);

  useEffect(() => {
    console.log('Initializing MSAL inside EntraAuthProvider...');
    ensureMsalInitialized()
      .then(() => {
        console.log('MSAL ready');
        setMsalReady(true);
      })
      .catch(err => console.error('[MSAL] init failed:', err));
  }, []);

  //if (!msalReady) return <LoadingPage message="Initializing…" />;

  if (process.env.NODE_ENV === 'development' && !isEntraConfigComplete()) {
    console.warn('[Entra] Config incomplete:', entraConfigDiagnostics);
  }

  return (
    <MsalProvider instance={msalInstance}>
      <EntraAuthProviderInner>{children}</EntraAuthProviderInner>
    </MsalProvider>
  );
}